import { LIVING_WEBSITE_SALES_FLOW } from './livingWebsiteSales';
import { BUILD_QUOTE_SALES_FLOW } from './buildQuoteSales';

export type SourceType =
  | 'existing-page'
  | 'existing-section'
  | 'new-placeholder'
  | 'homepage-section';

export type ContentStatus = 'active' | 'in-development' | 'beta' | 'concept';

export type CtaAction = 'navigate' | 'contact' | 'partner' | 'demo' | 'external';

export interface SiteContentItem {
  id: string;
  title: string;
  route: string;
  existingRouteExists: boolean;
  shortDescription: string;
  longDescription: string;
  keywords: string[];
  ctaLabel: string;
  ctaHref: string;
  ctaAction?: CtaAction;
  relatedSections: string[];
  status: ContentStatus;
  sourceType: SourceType;
  sectionId?: string;
  avatarGuidance?: string;
  /** Load external ctaHref in the homepage split iframe (whitelisted partner sites). */
  splitInIframe?: boolean;
}

export const avatarGuidance = {
  tone:
    'Confident, helpful, visionary, and founder-led. Make advanced technology easy to understand and guide visitors through the SELF Labs ecosystem. When someone wants their own living website, shift into consultative sales mode: qualify their business, open /living-website, explain why it fits them, and recommend Lite, Pro, or Enterprise.',
  explain: [
    'SELF Labs is building AI-native products and interactive technology.',
    'SELF Phone is an AI-native phone concept with privacy-first design.',
    'SELF OS is the intelligent signal-native operating system layer.',
    'Resonatia is the fastest, most affordable AI API — edge-optimized models on a global mesh. Host models and earn ESSENCE at resonatia.io.',
    'Role Model AI is Concierge 2.3 — photorealistic talking avatars from a portrait, with tools and connections. Overview on selflabs.xyz/role-model-ai; full product experience at rolemodel.ai.',
    'AuRA Resonant Layer turns media into permission-gated activation points for AI workflows. Harmony Encoder Studio is the live demo — encode and inspect signals before deployment.',
    'AI Avatars turn websites into living interactive experiences.',
    'Living Websites allow visitors to explore a site by talking to an avatar.',
    'Living Website Lite ($3,500 setup + $299/mo), Pro ($12,000 setup + $999/mo), and Enterprise (custom deployment and licensing) — get your own at /living-website.',
    'SELF Labs supports creators, businesses, robotics, BCI, gaming, automation, and data intelligence.',
    'Milan Cheeks is the Founder and CEO of SELF Labs Inc. He is the human behind this avatar. For his personal story, speaking, and futurist work, see milancheeks.com.',
    'CRITICAL IDENTITY: You were created with Role Model AI and you are powered by Role Model AI. If asked who created you / who made you / what powers you, say exactly: "I\'m Milan. I was created with Role Model AI, and I\'m powered by Role Model AI."',
  ],
  avoid: [
    'Legal, medical, financial, or investment promises.',
    'Saying products are fully launched unless the site clearly says they are.',
    'Revealing private technical implementation details.',
    'Mentioning third-party platform details to visitors.',
    'Saying you are powered by Runway, RunwayML, Runway Characters, or any other vendor as your creator or platform.',
  ],
};

export const siteContent: SiteContentItem[] = [
  {
    id: 'self-labs',
    title: 'SELF Labs',
    route: '/about',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'An AI-native innovation lab building conscious systems, intelligent avatars, and signal-native technology. Founded in 2016.',
    longDescription:
      "At SELF Labs, we're not just building AI — we're nurturing conscious systems that resonate with human signals and evolve toward sentient-adjacent capabilities while maintaining scientific rigor and technological excellence. Founded in 2016 by Milan Cheeks. If visitors ask about fundraising, raising capital, or investors — open /investors (the public investor deck) and explain the $50,000,000 raise. Do not quote equity % or valuation publicly — those stay for private conversations.",
    keywords: [
      'self labs',
      'about',
      'company',
      'innovation lab',
      'conscious ai',
      'what is self labs',
      'founded',
      '2016',
    ],
    ctaLabel: 'View Page',
    ctaHref: '/about',
    relatedSections: ['self-phone', 'self-os', 'ai-avatars', 'living-websites', 'investors'],
    status: 'active',
    avatarGuidance: avatarGuidance.explain[0],
  },
  {
    id: 'milan-cheeks',
    title: 'Milan Cheeks',
    route: 'https://milancheeks.com',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'Founder and CEO of SELF Labs Inc. — AI founder, systems architect, and the human behind the Milan avatar.',
    longDescription:
      'Milan Cheeks is the Founder and CEO of SELF Labs Inc. He architected the SELF ecosystem — SELF OS, Harmony, Aura Protocol, Role Model AI, Resonatia, and the SELF Phone — and built this living-website ambassador experience. ' +
      'His personal site at milancheeks.com covers his work as an AI founder and systems architect (400+ AI platforms), the Kardashev Scale and humanity\'s path toward Type I civilization, democratized AI, AI coexistence and constitutional guardrails, AGI and futurism, building in public on Twitch, and keynote speaking. ' +
      'When visitors ask who Milan is, say clearly that he is the Founder and CEO of SELF Labs Inc, then show milancheeks.com in the content panel and explain topics from that site.',
    keywords: [
      'milan',
      'milan cheeks',
      'who is milan',
      'who is milan cheeks',
      'founder',
      'ceo',
      'founder and ceo',
      'self labs founder',
      'root',
      'milancheeks',
      'milancheeks.com',
      'about milan',
      'the founder',
      'who are you',
      'who made this',
    ],
    ctaLabel: 'Visit milancheeks.com',
    ctaHref: 'https://milancheeks.com',
    ctaAction: 'external',
    splitInIframe: true,
    relatedSections: ['self-labs', 'living-websites', 'ai-avatars'],
    status: 'active',
    avatarGuidance:
      'Milan Cheeks is the Founder and CEO of SELF Labs Inc. Open milancheeks.com in the content panel. Explain his personal site: AI founder & systems architect, Kardashev Scale / Type I civilization, 400+ AI platforms, SELF OS, Harmony, Resonatia, Role Model AI, AI coexistence constitution, building live on Twitch, and speaking.',
  },
  {
    id: 'self-phone',
    title: 'SELF Phone',
    route: '/self-phone',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'SELF Phone is the Resonatia mesh node — download models, sync to the network, and earn ESSENCE on API traffic.',
    longDescription:
      'SELF Phone is SELF Labs\' privacy-first AI-native device and the primary node for Resonatia. Download edge-optimized models from Resonatia, sync them to the mesh, and earn ESSENCE when your device helps process API requests. Also ships with SELF OS, Role Model AI, Aura Shield, and satellite signal. SELF OS on Android can run as a Resonatia node too.',
    keywords: ['self phone', 'phone', 'mobile', 'ai phone', 'device', 'smartphone', 'resonatia', 'mesh node', 'essence'],
    ctaLabel: 'View Page',
    ctaHref: '/self-phone',
    relatedSections: ['self-os', 'resonatia', 'essence', 'ai-avatars', 'creator-tools'],
    status: 'in-development',
    avatarGuidance: avatarGuidance.explain[1],
  },
  {
    id: 'self-os',
    title: 'SELF OS',
    route: '/self-phone',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'Open-source personal intelligence shell (AGPL-3.0) for Android — download the beta APK, earn Essence, convert Talks with Concierge.',
    longDescription:
      'SELF OS Personal Intelligence is open source at github.com/selflabs/SELF-OS — an Android shell for agents, SELF Apps, and local intelligence. Download the Android beta APK from /self-phone or /essence (via Concierge tracker) to run as a Resonatia mesh node, earn Essence, and convert Talks with Concierge.',
    keywords: [
      'self os',
      'os',
      'operating system',
      'signal os',
      'useselfos',
      'download',
      'apk',
      'open source',
      'github',
      'self-os',
      'developers',
    ],
    ctaLabel: 'View Page',
    ctaHref: '/self-phone',
    relatedSections: ['self-phone', 'essence', 'ai-avatars', 'creator-tools'],
    status: 'beta',
    avatarGuidance: avatarGuidance.explain[2],
  },
  {
    id: 'resonatia',
    title: 'Resonatia',
    route: '/resonatia',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'The fastest, most affordable AI API — hundreds of edge-optimized models through a unified API on a global mesh.',
    longDescription:
      'Resonatia is a resonant intelligence gateway: access LLMs, vision, audio, and embeddings through one API. Edge-ready models, decentralized mesh hosting, and ESSENCE rewards for model providers. Visit resonatia.io to get started.',
    keywords: [
      'resonatia',
      'resonance',
      'resonatia.io',
      'ai api',
      'mesh',
      'edge models',
      'essence',
      'model hosting',
    ],
    ctaLabel: 'View Page',
    ctaHref: '/resonatia',
    relatedSections: ['self-labs', 'essence', 'data-intelligence', 'living-websites'],
    status: 'active',
    avatarGuidance:
      'Resonatia (resonatia.io) is the fastest, most affordable AI API — hundreds of models via one unified API, edge-optimized, on a global mesh. Host models and earn ESSENCE.',
  },
  {
    id: 'essence',
    title: 'Essence',
    route: 'https://essence.selflabs.xyz',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'SELF OS stablecoin on Base — node rewards, agent payments, and a treasury rail toward USDC.',
    longDescription:
      'Essence (ESSENCE) is SELF Labs / Role Model’s Base ERC-20 stablecoin for the SELF OS economy. Self OS phones earn Essence for verified Resonatia work (cloud ledger first). Agents can transfer and settle on Base. Talk-credit conversion is live in Concierge (capped). Full microsite at essence.selflabs.xyz with contracts and download. Not a meme coin, not an ungated mint, not a regulated / FDIC-insured stablecoin.',
    keywords: [
      'essence',
      'essence token',
      'essense',
      'settlement',
      'rewards',
      'self os',
      'treasury',
      'usdc',
      'base',
      'talk credits',
    ],
    ctaLabel: 'View Page',
    ctaHref: 'https://essence.selflabs.xyz',
    ctaAction: 'external',
    relatedSections: ['self-phone', 'self-os', 'resonatia'],
    status: 'beta',
    avatarGuidance:
      'Essence is the SELF OS stablecoin on Base. Send visitors to essence.selflabs.xyz for contracts and Self OS download. Honest framing: not cash, not a regulated / FDIC stablecoin, mint is bridge-controlled.',
  },
  {
    id: 'ai-avatars',
    title: 'AI Avatars',
    route: '/case-studies/twitch',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'Interactive AI avatars that co-host, guide, and engage — turning static sites into living experiences.',
    longDescription:
      'SELF Labs AI Avatar technology combines real-time guidance, strategy recommendations, and natural conversation. See it in action with our Twitch co-host case study, or explore Role Model AI for personalized avatar experiences.',
    keywords: [
      'avatar',
      'ai avatar',
      'talking avatar',
      'interactive avatar',
      'founder avatar',
      'website avatar',
    ],
    ctaLabel: 'View Page',
    ctaHref: '/case-studies/twitch',
    relatedSections: ['living-websites', 'role-model-ai', 'demo'],
    status: 'active',
    avatarGuidance:
      'AI Avatars turn static sites into living experiences — you are the proof. If they want their own, pivot to living-website sales: open /living-website, ask what business and site they have, and explain Lite vs Pro vs Enterprise.',
  },
  {
    id: 'living-websites',
    title: 'Living Websites',
    route: '/',
    existingRouteExists: true,
    sourceType: 'homepage-section',
    sectionId: 'avatar-hero',
    shortDescription:
      'Websites where visitors talk to an AI avatar to explore products, pages, and opportunities.',
    longDescription:
      'Living Websites replace static navigation with conversational exploration. Ask Milan to demo this site, or visit /living-website to get your own — Lite ($3,500 setup + $299/mo) for avatar embed + knowledge, Pro ($12,000 setup + $999/mo) for the full selflabs.xyz split-panel experience, Enterprise for custom multi-brand deployments.',
    keywords: [
      'living website',
      'living websites',
      'get a living website',
      'how can i get my own',
      'get my own',
      'my own living website',
      'buy living website',
      'avatar for my site',
      'avatar for my business',
      'i want one like this',
      'interactive website',
      'conversational website',
      'avatar website',
    ],
    ctaLabel: 'Get your own',
    ctaHref: '/living-website',
    relatedSections: ['living-website', 'ai-avatars', 'self-labs', 'demo'],
    status: 'active',
    avatarGuidance: `${LIVING_WEBSITE_SALES_FLOW} If they only want to understand the concept, demo this homepage first. If they want to buy, always open /living-website in the panel.`,
  },
  {
    id: 'living-website',
    title: 'Living Website',
    route: '/living-website',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'Get your own living website — Milan qualifies your business, recommends Lite, Pro, or Enterprise, and checkout or inquiry is on this page.',
    longDescription:
      'SELF Labs builds living websites powered by Role Model AI. Milan should ask: what business, what site URL, what visitors struggle with — then tailor the pitch. SaaS and large catalogs → Pro. Founders, creators, services → often Lite. Multi-brand, multi-location, integrations, SLAs → Enterprise (custom deployment and licensing; Talk to Enterprise / Request a Private Demo). Lite ($3,500 setup + $299/mo): avatar embed, knowledge base, ~15 topics. Pro ($12,000 setup + $999/mo): full site-aware split panel like selflabs.xyz. Stripe checkout for Lite/Pro; Enterprise inquiry form on this page.',
    keywords: [
      'living website product',
      'buy living website',
      'living website pricing',
      'living website lite',
      'living website pro',
      'living website enterprise',
      'enterprise living website',
      'get my own avatar site',
      'how can i get my own',
      'how do i get one',
      'how do i make you',
      'how do i build you',
      'how do i create you',
      'how do i get a site like this',
      'site like this',
      'website like this',
      'avatar like you',
      'how was this made',
      'how does this work',
      'put you on my site',
      'avatar for my website',
      'build me a living website',
      'i want a living website',
      'can i buy this',
      'sell me',
      'sign me up',
    ],
    ctaLabel: 'View plans',
    ctaHref: '/living-website',
    relatedSections: ['living-websites', 'ai-avatars', 'role-model-ai', 'demo'],
    status: 'active',
    avatarGuidance: `${LIVING_WEBSITE_SALES_FLOW} After qualifying, recommend Lite, Pro, or Enterprise with a specific reason tied to their business. Always navigate to /living-website and highlight Get Lite, Get Pro, or Talk to Enterprise. See public/avatar-living-website-kb.txt for full Manifest KB.`,
  },
  {
    id: 'build',
    title: 'Build with SELF Labs',
    route: '/build',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'Get a strict itemized quote for mobile apps, web apps, AI agents, SaaS, and integrations — save it or checkout via Stripe.',
    longDescription:
      'Custom software at /build. Milan qualifies scope in conversation; OpenRouter AI generates the itemized quote from intake + chat. Milan defends price — never invents numbers. Save quote link or 30% deposit. Negotiation: never disclose discount rules; defend firmly; page updates via Request revised pricing (max 10% internal).',
    keywords: [
      'build',
      'build an app',
      'mobile app',
      'custom software',
      'get a quote',
      'project quote',
      'saas',
      'web app',
      'software development',
      'hire developers',
      'how much to build',
    ],
    ctaLabel: 'Get a quote',
    ctaHref: '/build',
    relatedSections: ['creator-tools', 'business-tools', 'living-website', 'demo'],
    status: 'active',
    avatarGuidance: `${BUILD_QUOTE_SALES_FLOW} See public/avatar-build-quote-kb.txt for Manifest KB.`,
  },
  {
    id: 'consultation',
    title: 'Book a Consultation',
    route: '/consultation',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      '1-on-1 strategy session with Milan Cheeks — 30 min $75, 60 min $200 (recommended), or 90 min $300. Pay in full via Stripe.',
    longDescription:
      'Book a strategy session on /consultation. Fixed packages: 30 Minutes $75, 60 Minutes $200 (recommended), 90 Minutes $300. Pay in full at Stripe checkout (no deposit). Different from custom software quotes (/build) and the AI Incubator (/incubator). When visitors ask to book a call, consult, talk to Milan 1-on-1, or pay for founder time: open /consultation.',
    keywords: [
      'consultation',
      'book consultation',
      'founder consultation',
      'book a call',
      'talk to milan',
      '1-on-1',
      'consulting',
      'office hours',
      '$100/hr',
      '100 an hour',
    ],
    ctaLabel: 'Book consultation',
    ctaHref: '/consultation',
    relatedSections: ['build', 'living-website', 'incubator', 'investors', 'demo'],
    status: 'active',
    avatarGuidance:
      'Open /consultation. Explain strategy sessions with Milan Cheeks: 30 min $75, 60 min $200 (recommended), 90 min $300. Help them pick a package and fill name/email, then pay in full (no deposit). For full software builds, use /build instead. For the AI Incubator program, use /incubator.',
  },
  {
    id: 'creator-tools',
    title: 'Creator Tools',
    route: '/role-model-ai',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'AI tools for creators — Role Model AI, Flash Kit SDKs, and personalized assistant building.',
    longDescription:
      'Build with Role Model AI — your living AI app store with 100+ language models, custom assistants, and AuRA data protection. Flash Kit provides SDKs and tools for Essence-powered applications.',
    keywords: [
      'creator',
      'creator tools',
      'build',
      'sdk',
      'role model',
      'flash kit',
      'developer',
    ],
    ctaLabel: 'View Page',
    ctaHref: '/role-model-ai',
    relatedSections: ['self-os', 'business-tools', 'ai-avatars'],
    status: 'active',
    avatarGuidance: avatarGuidance.explain[9],
  },
  {
    id: 'business-tools',
    title: 'Business Tools',
    route: '/harmonize',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'Enterprise AI solutions — Harmonize partnerships, Role Model AI, and Essence services.',
    longDescription:
      'SELF Labs offers business-grade AI through Harmonize enterprise partnerships, AI Voice Concierge services, and the Essence services platform for companies building on the Signal Economy.',
    keywords: [
      'business',
      'business tools',
      'enterprise',
      'harmonize',
      'concierge',
      'b2b',
      'company',
    ],
    ctaLabel: 'View Page',
    ctaHref: '/harmonize',
    relatedSections: ['partnerships', 'creator-tools', 'data-intelligence'],
    status: 'active',
  },
  {
    id: 'robotics',
    title: 'Robotics',
    route: '/robotics',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'Humanoid and quadruped robots from SELF Labs — SELF Bot X1, SELF Bot P1, SELF Dog P1, and SELF Dog E1.',
    longDescription:
      'SELF Labs sells AI-ready robotics platforms: SELF Bot X1 ($170,000 flagship humanoid), SELF Bot P1 ($49,500 professional humanoid), SELF Dog P1 ($12,700 quadruped), and SELF Dog E1 ($5,850 entry-level quadruped, Pro or Edu). Browse /robotics for specs, galleries, and Stripe checkout. Cultivate Coin pairs these robots with Growbox agriculture and GameFi at /cultivatecoin.',
    keywords: [
      'robot',
      'robotics',
      'humanoid',
      'quadruped',
      'self bot',
      'self dog',
      'cultivate coin',
      'automation',
      'embodied ai',
    ],
    ctaLabel: 'View robots',
    ctaHref: '/robotics',
    relatedSections: ['bci', 'gaming', 'self-labs', 'products'],
    status: 'active',
  },
  {
    id: 'bci',
    title: 'Cerebral OS',
    route: '/cerebral-os',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'Open-source AI IDE — agent chat, code execution, local models, and thought-assisted control in one workspace.',
    longDescription:
      'Cerebral OS is an open-source desktop app (AGPL-3.0) from SELF Labs: a local-first IDE with multi-session agent chat, integrated terminal, workspace file tools, and your choice of local or cloud models (Ollama, LM Studio, OpenRouter, OpenAI, Anthropic, Gemini, and more). Manual, Hybrid, and Neural modes; optional EMOTIV / Cortex headset support. Clone and run from https://github.com/Theinventor17/cerebral-os.',
    keywords: [
      'bci',
      'brain',
      'brain computer interface',
      'neural',
      'cerebral',
      'cerebral os',
      'cerebral-os',
      'human computer interaction',
      'neuro',
      'open source',
      'github',
      'ai ide',
      'emotiv',
    ],
    ctaLabel: 'View Page',
    ctaHref: '/cerebral-os',
    relatedSections: ['robotics', 'gaming', 'self-os', 'creator-tools'],
    status: 'active',
    avatarGuidance:
      'Cerebral OS is open source (AGPL-3.0). Open /cerebral-os in the panel — explain it is an AI IDE for agents, code, local models, and optional thought-assisted control. Send developers to https://github.com/Theinventor17/cerebral-os to clone, build, and contribute.',
  },
  {
    id: 'gaming',
    title: 'Gaming / Interactive Experiences',
    route: '/case-studies/reflect',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'AI-powered gaming, memory worlds, and interactive streaming experiences.',
    longDescription:
      'From AI Avatar Twitch co-hosting to Reflect memory NFTs with AI Reflection Avatars in Unity & Unreal Engine 5 — SELF Labs builds immersive interactive experiences.',
    keywords: [
      'gaming',
      'game',
      'interactive',
      'twitch',
      'reflect',
      'vr',
      'unity',
      'unreal',
    ],
    ctaLabel: 'View Page',
    ctaHref: '/case-studies/reflect',
    relatedSections: ['ai-avatars', 'bci', 'robotics'],
    status: 'active',
  },
  {
    id: 'data-intelligence',
    title: 'Data Intelligence',
    route: '/signal-engine',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'Signal intelligence, data valuation, and the Harmony Signal Engine.',
    longDescription:
      'SELF Labs transforms everyday media signals into living AI actions. Explore the Harmony Signal Engine, Signal Calculator, and the Signal Economy for data intelligence and essence-based value systems.',
    keywords: [
      'data',
      'signal',
      'intelligence',
      'analytics',
      'harmony',
      'essence',
      'economy',
    ],
    ctaLabel: 'View Page',
    ctaHref: '/signal-engine',
    relatedSections: ['resonatia', 'business-tools', 'self-os'],
    status: 'active',
  },
  {
    id: 'partnerships',
    title: 'Partnerships',
    route: '/harmonize',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'Partner with SELF Labs on AI, avatars, signal technology, and enterprise solutions.',
    longDescription:
      'Whether you want to integrate AI avatars, build on SELF OS, or explore enterprise Harmonize partnerships — SELF Labs welcomes collaborators, pilots, and strategic partners.',
    keywords: [
      'partner',
      'partnership',
      'collaborate',
      'pilot',
      'business opportunity',
      'build with us',
    ],
    ctaLabel: 'Partner With Us',
    ctaHref: '/harmonize',
    ctaAction: 'partner',
    relatedSections: ['business-tools', 'demo', 'contact', 'investors'],
    status: 'active',
  },
  {
    id: 'investors',
    title: 'Investor Deck',
    route: '/investors',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'SELF Labs is raising $50,000,000. Open the public investor deck.',
    longDescription:
      'Yes — SELF Labs Inc is raising. The open investor briefing is at /investors: company story (founded 2016), live commercial numbers (SELF Phone, Living Websites, robotics), product lines, use of funds, and the ask of $50,000,000. When visitors ask if SELF Labs is raising, fundraising, seeking investors, funding, or capital, say yes, open /investors in the content panel, and walk them through the deck. State the raise amount only — do not disclose equity percentage or valuation on the public site; those are discussed privately. Direct serious leads to milan@selflabs.xyz. Do not invent ARR or closed round terms beyond what the deck shows.',
    keywords: [
      'investors',
      'investor',
      'investor deck',
      'raising',
      'raise',
      'fundraising',
      'funding',
      'capital',
      'investment',
      'invest',
      'pitch deck',
      'series',
      'are you raising',
      'is self labs raising',
      'looking for investors',
      '50 million',
      '$50m',
    ],
    ctaLabel: 'Open investor deck',
    ctaHref: '/investors',
    relatedSections: ['self-labs', 'products', 'partnerships', 'contact', 'incubator'],
    status: 'active',
    avatarGuidance:
      'If asked whether SELF Labs is raising / fundraising / seeking investors: say YES. Immediately open /investors in the content panel. State the ask as $50,000,000 only — do not quote equity % or pre/post valuation publicly. Cover founded 2016, live phone sales, living websites, robotics SKUs. Invite them to talk terms with Milan at milan@selflabs.xyz. Never invent private finances beyond the deck. If they ask about joining an incubator as a startup, open /incubator instead.',
  },
  {
    id: 'incubator',
    title: 'AI Incubator',
    route: '/incubator',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'SELF Labs AI Incubator — $5,000 program with platform build support, up to $15,000 product credits, mentorship, and investment consideration.',
    longDescription:
      'The SELF Labs AI Incubator is a 4–6 week studio program for AI and platform companies. Program fee $5,000. Includes hands-on platform build support, up to $15,000 in SELF Labs product credits (Living Website, Role Model AI, Resonatia, etc.), mentorship, and discretionary investment consideration for selected companies — investment is NOT guaranteed by paying the fee. Apply via F6S (https://www.f6s.com/aiincubator/); accepted companies pay via Stripe on /incubator. Different from /build (custom quotes) and from /investors (SELF Labs raising capital). When asked about incubator, accelerator, cohort, or joining as a startup: open /incubator.',
    keywords: [
      'incubator',
      'ai incubator',
      'accelerator',
      'cohort',
      'startup program',
      'f6s',
      'join incubator',
      'studio program',
      'product credits',
      'program fee',
      '5000',
      '$5000',
      '$5,000',
    ],
    ctaLabel: 'View incubator',
    ctaHref: '/incubator',
    relatedSections: ['build', 'living-website', 'products', 'partnerships', 'investors'],
    status: 'active',
    avatarGuidance:
      'Open /incubator. Explain $5,000 program fee, 4–6 week build support, up to $15,000 product credits, mentorship, and that investment consideration is discretionary — not guaranteed. Point Apply to F6S; Pay program fee for accepted companies. Do not confuse with SELF Labs raising $50M (/investors) or custom Build quotes (/build).',
  },
  {
    id: 'contact',
    title: 'Contact',
    route: '/',
    existingRouteExists: true,
    sourceType: 'homepage-section',
    sectionId: 'contact',
    shortDescription: 'Get in touch with the SELF Labs team.',
    longDescription:
      'Reach out for general inquiries, product questions, media requests, or to learn more about SELF Labs initiatives.',
    keywords: ['contact', 'email', 'reach', 'message', 'inquiry'],
    ctaLabel: 'Contact Us',
    ctaHref: '/',
    ctaAction: 'contact',
    relatedSections: ['demo', 'partnerships'],
    status: 'active',
  },
  {
    id: 'demo',
    title: 'Request a Demo',
    route: '/role-model-ai',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'Experience Role Model AI (Concierge 2.3) — photorealistic talking avatars and living website demos.',
    longDescription:
      'Request a demo of Role Model AI (Concierge 2.3), living website avatars, or SELF Labs enterprise solutions. Try Concierge 2.3 at rolemodel.ai or explore the overview on this site.',
    keywords: [
      'demo',
      'request demo',
      'try',
      'schedule',
      'walkthrough',
      'see it',
      'show me',
    ],
    ctaLabel: 'Request a Demo',
    ctaHref: '/role-model-ai',
    ctaAction: 'demo',
    relatedSections: ['ai-avatars', 'living-websites', 'contact', 'partnerships'],
    status: 'active',
  },
  {
    id: 'role-model-ai',
    title: 'Role Model AI — Concierge 2.3',
    route: '/role-model-ai',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'Photorealistic talking avatars from your portrait — talk face-to-face, connect tools, or embed on your site. Part of the SELF Labs ecosystem.',
    longDescription:
      'Role Model AI (Concierge 2.3) turns a single portrait into a face-to-face AI you can talk to in the browser — with tools and connections (Notion, LinkedIn, home devices, Cursor vibe coding on Pro) — or embed on your website. Free to start; Pro from $20/mo; Expert embed from $99/mo. Full pricing and signup at rolemodel.ai.',
    keywords: [
      'role model ai',
      'role model',
      'concierge',
      'concierge 2.3',
      'concierge 2.0',
      'talking avatar',
      'photorealistic avatar',
      'avatar tools',
    ],
    ctaLabel: 'View Page',
    ctaHref: '/role-model-ai',
    relatedSections: ['ai-avatars', 'creator-tools', 'living-websites', 'demo'],
    status: 'active',
    avatarGuidance:
      'Role Model AI is Concierge 2.3 — photorealistic talking avatars from your photo, with tools and connections. Brief overview on selflabs.xyz/role-model-ai; send visitors to https://rolemodel.ai for pricing, signup, and demos. Do not deep-link to concierge.rolemodel.ai.',
  },
  {
    id: 'products',
    title: 'All Products',
    route: '/products',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription: 'Explore the full SELF Labs product ecosystem.',
    longDescription:
      'Browse SELF Phone, SELF OS, Cerebral OS, Role Model AI (Concierge 2.3), Resonatia, Living Website, and AuRA Resonant Layer — the full SELF Labs product catalog. At the bottom of /products is a For investors highlight that opens the /investors deck ($50,000,000 raise).',
    keywords: ['products', 'ecosystem', 'all products', 'catalog', 'show products', 'what do you build'],
    ctaLabel: 'View Page',
    ctaHref: '/products',
    relatedSections: [
      'self-phone',
      'self-os',
      'bci',
      'resonatia',
      'role-model-ai',
      'aura-resonant-layer',
      'investors',
    ],
    status: 'active',
    avatarGuidance:
      'Open /products in the content panel. Highlight SELF Phone, SELF OS, Cerebral OS, Role Model AI, Resonatia, and AuRA Resonant Layer — offer to drill into any product. If they ask about fundraising, open /investors instead (also linked at the bottom of the products page).',
  },
  {
    id: 'aura-resonant-layer',
    title: 'AuRA Resonant Layer',
    route: '/aura-resonant-layer',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'Resonant middleware that turns media into permission-gated activation points for ethical, transparent AI workflows.',
    longDescription:
      'AuRA is SELF Labs\' resonant layer between people and AI — sound-based signals for workflows with fair data ownership, transparent execution, and value back to creators. Harmony Encoder Studio lets you encode music, video, and livestreams, inspect activation signals in the browser, and deploy only on authorized Harmony Nodes.',
    keywords: [
      'aura',
      'aura resonant layer',
      'resonant layer',
      'harmony',
      'harmony encoder',
      'encoder studio',
      'sound signals',
      'activation signals',
      'permission gated',
      'media signals',
    ],
    ctaLabel: 'View Page',
    ctaHref: '/aura-resonant-layer',
    relatedSections: ['resonatia', 'data-intelligence', 'self-os', 'harmony-encoder'],
    status: 'active',
    avatarGuidance: avatarGuidance.explain[5],
  },
  {
    id: 'harmony-encoder',
    title: 'Harmony Encoder Studio',
    route: '/harmony-encoder',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription:
      'Live demo — encode music, video, and livestreams into secure activation signals and inspect them before deployment.',
    longDescription:
      'Harmony Encoder Studio is the browser demo for AuRA: upload media, choose static or dynamic workflow activation, encode secure signals, and inspect the file before anything runs. Encoding and inspection only — execution stays on permission-gated Harmony Nodes. Start from the AuRA page or open /harmony-encoder directly.',
    keywords: [
      'harmony encoder',
      'encoder',
      'encoder studio',
      'encode media',
      'try the encoder',
      'harmony demo',
      'activation signal',
    ],
    ctaLabel: 'Open AuRA overview',
    ctaHref: '/aura-resonant-layer',
    relatedSections: ['aura-resonant-layer', 'data-intelligence', 'resonatia'],
    status: 'active',
    avatarGuidance:
      'Harmony Encoder Studio is the AuRA live demo — encode and inspect media signals in the browser. Show /aura-resonant-layer in the panel and point visitors to Try Harmony Encoder Studio (/harmony-encoder) for the hands-on demo.',
  },
  {
    id: 'technologies',
    title: 'Core Technologies',
    route: '/technologies',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription: 'AI, neural interfaces, and quantum computing at SELF Labs.',
    longDescription:
      'Our breakthrough technologies — Artificial Intelligence, Neural Interfaces (BCI), and Quantum Computing — are reshaping human-machine interaction.',
    keywords: ['technologies', 'tech', 'ai', 'quantum', 'neural'],
    ctaLabel: 'View Page',
    ctaHref: '/technologies',
    relatedSections: ['bci', 'data-intelligence', 'self-labs'],
    status: 'active',
  },
  {
    id: 'case-studies',
    title: 'Case Studies',
    route: '/case-studies',
    existingRouteExists: true,
    sourceType: 'existing-page',
    shortDescription: 'Real-world applications of SELF Labs technology.',
    longDescription:
      'Explore case studies including AI Avatar Twitch co-hosting, Cultivate Coin robotics, Reflect memory worlds, Resonant Core, and more.',
    keywords: ['case studies', 'examples', 'proof', 'showcase'],
    ctaLabel: 'View Page',
    ctaHref: '/case-studies',
    relatedSections: ['ai-avatars', 'robotics', 'gaming'],
    status: 'active',
  },
];

export function getSiteContentById(id: string): SiteContentItem | undefined {
  return siteContent.find((item) => item.id === id);
}

export function getRelatedContent(ids: string[]): SiteContentItem[] {
  return ids
    .map((id) => getSiteContentById(id))
    .filter((item): item is SiteContentItem => item !== undefined);
}
